import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";

// Components
import IkTable from "@Components/IkTable";

// Utils
import dataHandler from "@Utils/dataHandler";

// Styles
import styles from "./styles.module.css";

const YearMonthTable = ({ dispatch, userName, companyName, onOpenFilterModal, appliedFilters = {}, isPromo }) => {
	const defaultParams = { page: 1, orderDir: "asc", rows: 25 };
	const [filteredItems, setFilteredItems] = useState([]);
	const [filteredCount, setFilteredCount] = useState(0);
	const [filteredPrintData, setFilteredPrintData] = useState([]);
	const [lastParams, setLastParams] = useState(defaultParams);
	const [sumData, setSumData] = useState({});

	useEffect(() => {
		const mergedParams = {
			rows: defaultParams.rows,
			page: defaultParams.page,
			orderDir: defaultParams.orderDir,
			...appliedFilters,
		};

		(async () => {
			try {
				const response = await dataHandler({
					dispatch,
					method: "GET",
					url: "/reports/historic/monthyear",
					params: mergedParams,
				});
				setFilteredItems(response.items);
				setFilteredCount(response.count);
				setSumData(response.total || {});
				setLastParams(mergedParams);
			} catch (error) {
				setFilteredItems([]);
				setFilteredCount(0);
				setSumData({});
			}
		})();
	}, [appliedFilters]);

	const handlerData = async (params = defaultParams, printable) => {
		const mergedParams = {
			rows: params.rows,
			page: params.page,
			orderDir: params.orderDir,
			...appliedFilters,
		};

		const data = await dataHandler({
			method: "GET",
			dispatch,
			url: "/reports/historic/monthyear",
			params: mergedParams,
		});
		if (printable) {
			setFilteredPrintData(data.items);
		} else {
			setFilteredItems(data.items);
			setFilteredCount(data.count);
			setLastParams(mergedParams);
			setSumData(data.total || {});
		}
	};

	const handleRefreshData = () => {
		handlerData(lastParams);
	};

	const renderTopButtons = () => {
		const hasFilters = Object.keys(appliedFilters || {}).length > 0;
		const filterText = hasFilters ? "Filtrando" : "";

		const buttons = [
			{
				icon: "filter",
				className: hasFilters ? styles.filterButtonActive : styles.filterButton,
				onClick: onOpenFilterModal,
				content: filterText,
				label: filterText,
				showLabel: true,
				componentType: "button",
			},
			{
				icon: "refresh",
				className: styles.refreshButton,
				onClick: handleRefreshData,
			},
		];

		const dateRange =
			appliedFilters?.createdDate || appliedFilters?.closeDate || appliedFilters?.["chat.closeDate"] || appliedFilters?.date;
		if (dateRange) {
			const formatD = (d) => {
				if (!d) return "";
				const parts = d.split("-");
				if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
				if (parts.length === 2) return `${parts[1]}/${parts[0]}`;
				return d;
			};
			const [start, end] = dateRange.split(",");
			let value = formatD(start);
			const today = new Date();
			const currentEnd =
				end || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
			if (currentEnd && start !== currentEnd) value += ` al ${formatD(currentEnd)}`;

			buttons.push({
				componentType: "Label",
				title: "Fecha:",
				value,
			});
		}

		return buttons;
	};

	const columns = [
		{
			label: "Año",
			key: "year",
			format: "string",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
		},
		{
			label: "Mes",
			key: "month",
			format: "string",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
		},
		{ label: "Conversaciones", key: "chats", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 },
	];

	if (isPromo) {
		columns.push({ label: "Promo", key: "promoCount", format: "blankZero", headerPosition: "center", dataPosition: "center", width: 1 });
	}

	columns.push(
		{
			label: "Cant. Mensajes",
			key: "messages",
			format: "number",
			headerPosition: "center",
			dataPosition: "center",
			width: 1,
		},
		{
			label: "Nuevos Contactos",
			key: "contacts",
			format: "blankZero",
			headerPosition: "left",
			dataPosition: "left",
			width: 15,
		}
	);

	return (
		<div className={styles.container}>
			<div className={styles.containerTableGroup}>
				<div className={styles.tableGroupList}>
					<IkTable
						data={filteredItems}
						columns={columns}
						totalRows={filteredCount}
						filteredPrintData={filteredPrintData}
						defaultParams={defaultParams}
						dataHandler={handlerData}
						print={{
							printHeader: {
								company: companyName,
								title: "Año/Mes",
								user: userName,
							},
						}}
						searcher={{
							searchable: false,
							searchPosition: "flex-start",
							searchDisabled: false,
							filterButton: false,
							width: "40rem",
						}}
						topButtons={renderTopButtons()}
						sumData={sumData}
					/>
				</div>
			</div>
		</div>
	);
};

function mapStateToProps({ user, company }) {
	return {
		userName: user.name,
		companyName: company.name,
		isPromo: company?.settings?.isPromo,
	};
}

YearMonthTable.propTypes = {
	companyName: PropTypes.string,
	userName: PropTypes.string,
	dispatch: PropTypes.func,
	onOpenFilterModal: PropTypes.func,
	appliedFilters: PropTypes.object,
	isPromo: PropTypes.bool,
};

export default connect(mapStateToProps)(YearMonthTable);
